/**
 * 前端 API 请求客户端
 * 自动处理加密响应的解密
 * GET 请求参数自动加密
 */

import { decrypt, isEncryptionEnabled } from './client-encrypt';
import { encodeRequestParams } from '@/lib/request-encoder';
import { getAuthToken } from '@/components/auth-provider';

interface FetchOptions extends RequestInit {
  params?: Record<string, string | number | boolean>;
}

interface EncryptedResponse {
  encrypted: boolean;
  data: string;
  timestamp: number;
}

export class ApiRequestError extends Error {
  status: number;
  needRiskChallenge: boolean;

  constructor(status: number, message: string, needRiskChallenge = false) {
    super(message);
    this.name = "ApiRequestError";
    this.status = status;
    this.needRiskChallenge = needRiskChallenge;
  }
}

/**
 * 安全的 fetch 客户端
 * 自动加密请求参数 + 自动解密加密响应
 */
export async function secureFetch<T = unknown>(
  url: string,
  options: FetchOptions = {}
): Promise<T> {
  const { params, ...fetchOptions } = options;

  // 构建 URL（自动加密参数）
  let finalUrl = url;
  if (params) {
    const encoded = encodeRequestParams(params);
    finalUrl = `${url}?${encoded}`;
  }

  // 发送请求
  // 管理接口使用 Basic Auth（浏览器自动携带），不注入会员 Bearer token。
  // 部分历史安全管理接口仍位于 /api/security/*；它们同样只接受 Basic Auth。
  // 若在已登录会员账号的浏览器里覆盖 Authorization，会让后台接口稳定返回 401。
  const isAdminApi = url.startsWith('/api/admin')
    || url.startsWith('/api/security/blacklist')
    || url.startsWith('/api/security/whitelist')
    || url.startsWith('/api/security/monitor')
    || url.startsWith('/api/security/subnets')
    || url.startsWith('/api/security/sync-cache');
  const bearerToken = typeof window !== "undefined" && getAuthToken() ? getAuthToken() : null;
  const response = await fetch(finalUrl, {
    ...fetchOptions,
    headers: {
      'Content-Type': 'application/json',
      ...(bearerToken && !isAdminApi ? { Authorization: `Bearer ${bearerToken}` } : {}),
      ...fetchOptions.headers,
    },
  });

  const contentType = response.headers.get('content-type') || '';
  const rawData = contentType.includes('application/json')
    ? await response.json().catch(() => null)
    : null;

  if (!response.ok) {
    const serverMessage = rawData && typeof rawData === 'object' && 'error' in rawData
      ? String((rawData as { error?: unknown }).error || '')
      : '';
    const needRiskChallenge = Boolean(
      rawData && typeof rawData === 'object' && (rawData as { needRiskChallenge?: unknown }).needRiskChallenge
    );
    throw new ApiRequestError(
      response.status,
      serverMessage || `HTTP ${response.status}`,
      needRiskChallenge,
    );
  }

  if (!rawData) {
    throw new ApiRequestError(response.status, 'Invalid JSON response');
  }

  // 检查是否是加密响应
  if (rawData && typeof rawData === 'object' && 'encrypted' in rawData && rawData.encrypted) {
    // 解密数据
    const encryptedResp = rawData as EncryptedResponse;
    return decrypt<T>(encryptedResp.data);
  }

  // 非加密响应，直接返回
  return rawData as T;
}

/**
 * GET 请求
 */
export async function secureGet<T = unknown>(
  url: string,
  params?: Record<string, string | number | boolean>
): Promise<T> {
  return secureFetch<T>(url, { method: 'GET', params });
}

/**
 * POST 请求
 */
export async function securePost<T = unknown>(
  url: string,
  body: unknown
): Promise<T> {
  return secureFetch<T>(url, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * DELETE 请求 (支持 query params)
 */
export async function secureDelete<T = unknown>(
  url: string,
  params?: Record<string, string | number | boolean>
): Promise<T> {
  return secureFetch<T>(url, { method: 'DELETE', params });
}

/**
 * 创建带基础 URL 的请求客户端
 */
export function createApiClient(baseUrl: string) {
  return {
    get: <T = unknown>(path: string, params?: Record<string, string | number | boolean>) =>
      secureGet<T>(`${baseUrl}${path}`, params),
    post: <T = unknown>(path: string, body: unknown) =>
      securePost<T>(`${baseUrl}${path}`, body),
    fetch: <T = unknown>(path: string, options?: FetchOptions) =>
      secureFetch<T>(`${baseUrl}${path}`, options),
  };
}
